import { KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import { Check, Lock, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { NamesInput, NumbersInput, SheetInput } from '@/reports/inputs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useFill } from './fillContext';

/**
 * What someone entered reads a weight heavier than a placeholder or an empty
 * dash, and a placeholder reads lighter still, so a filled-in value is never
 * mistaken for an example (FUS-345).
 */
export const ENTERED = 'font-medium placeholder:font-normal placeholder:text-muted-foreground/70';

/**
 * A sweep or table cell's editor, in a px-1.5 gutter: grey while empty, its box on hover, a primary border once
 * focused. No shadow-none, and no bg-transparent on a blank cell: Bootstrap's !important on those beats the amber
 * ring and any hover background.
 */
function cellBox(blank: boolean) {
  return cn('border-[1px] border-transparent hover:border-input hover:bg-background focus:border-primary focus:bg-background focus-visible:border-primary', blank ? 'bg-muted/60' : 'bg-transparent');
}
export const cellInput = (blank: boolean) => cn('h-8 w-full rounded-none px-1 focus-visible:ring-2 focus-visible:ring-inset', cellBox(blank), ENTERED);
export const cellSelect = (blank: boolean) => cn('h-8 w-full px-0.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50', cellBox(blank), blank ? 'text-muted-foreground/70' : 'font-medium');

/** A disabled field is locked, except in a preview of the sheet, where nothing is filled in to lock. */
function useLocked(disabled?: boolean): boolean {
  return !!disabled && !useFill().preview;
}

/** A locked field says so: a lock where typing would go. */
function Locked({ locked, className, children }: { locked: boolean; className?: string; children: ReactNode }) {
  return (
    <div className={cn('relative min-w-0', className)}>
      {children}
      {locked && <Lock aria-hidden className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />}
    </div>
  );
}

/** One input's frame: its question, a hint at the right, its help, then the control. */
export function Field({ input, hint, wide, children, anchor }: { input: Pick<SheetInput, 'id' | 'label' | 'help' | 'required'>; hint?: string; wide?: boolean; children: ReactNode; anchor?: string }) {
  return (
    <div id={anchor} className={cn('flex min-w-0 flex-col gap-1.5 scroll-mt-24', wide && 'md:col-span-2')}>
      <div className="flex items-baseline justify-between gap-[0.75rem]">
        <label htmlFor={`field-${input.id}`} className="text-sm font-medium text-foreground">
          {input.label}
          {input.required && <span className="ml-0.5 text-destructive" aria-label={I18n.get('rv2Required')}>*</span>}
        </label>
        {hint && <span className="shrink-0 text-xs text-muted-foreground">{hint}</span>}
      </div>
      {input.help && <p className="-mt-1 text-xs text-muted-foreground">{input.help}</p>}
      {children}
    </div>
  );
}

/** A number, typed freely and kept as a number; empty is null. The unit sits beside it. */
export function NumberControl({ id, value, unit, placeholder, disabled, onChange, className, inputClassName, ...rest }: {
  id?: string; value: number | null; unit?: string; placeholder?: string; disabled?: boolean; onChange: (value: number | null) => void; className?: string; inputClassName?: string;
} & Record<`data-${string}`, string | undefined> & { onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void; 'aria-label'?: string }) {
  const [text, setText] = useState(value === null ? '' : String(value));
  useEffect(() => {
    // follow a value changed from elsewhere, but not while the text already reads as it (e.g. "13." mid-typing)
    if (parseNumber(text) !== value) setText(value === null ? '' : String(value));
  }, [value]);
  const invalid = text.trim() !== '' && parseNumber(text) === null;
  const locked = useLocked(disabled);
  return (
    <div className={cn('flex min-w-0 items-stretch', className)}>
      <Locked locked={locked} className="flex flex-1">
        <Input
          id={id}
          inputMode="decimal"
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn('rounded-md tabular-nums', ENTERED, locked && 'pr-8', unit && 'rounded-r-none', inputClassName)}
          onChange={(e) => {
            setText(e.target.value);
            const parsed = parseNumber(e.target.value);
            if (parsed !== null || e.target.value.trim() === '') onChange(parsed);
          }}
          {...rest}
        />
      </Locked>
      {unit && <span className="flex items-center rounded-r-md border-[1px] border-l-0 border-input bg-muted/60 px-2.5 text-xs text-muted-foreground">{unit}</span>}
    </div>
  );
}

export function parseNumber(text: string): number | null {
  const trimmed = text.trim().replace('−', '-').replace(/,(\d+)$/, '.$1');
  if (trimmed === '' || !/^-?(\d+\.?\d*|\.\d+)(e-?\d+)?$/i.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function TextControl({ id, value, multiline, placeholder, disabled, onChange }: { id?: string; value: string; multiline?: boolean; placeholder?: string; disabled?: boolean; onChange: (value: string) => void }) {
  const locked = useLocked(disabled);
  return multiline
    ? <Textarea id={id} value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={cn('min-h-20', ENTERED)} />
    : <Locked locked={locked} className="w-full"><Input id={id} value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={cn('rounded-md', ENTERED, locked && 'pr-8')} /></Locked>;
}

export function DateControl({ id, value, disabled, onChange }: { id?: string; value: string; disabled?: boolean; onChange: (value: string) => void }) {
  const locked = useLocked(disabled);
  return (
    <Locked locked={locked} className="w-full">
      <Input id={id} type="date" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={cn('rounded-md', value ? ENTERED : 'text-muted-foreground/70', locked && 'pr-8')} />
    </Locked>
  );
}

/** A row of buttons, one chosen: choices, and yes/no. Choosing the chosen one again clears it. */
export function Segmented<T extends string | boolean>({ id, options, value, disabled, onChange, size = 'default' }: {
  id?: string; options: Array<{ value: T; label: string }>; value: T | null; disabled?: boolean; onChange: (value: T | null) => void; size?: 'default' | 'sm';
}) {
  return (
    <div id={id} role="radiogroup" className={cn('inline-flex w-fit max-w-full flex-wrap items-center gap-0.5 self-start rounded-lg bg-muted p-0.5', disabled && 'opacity-50')}>
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            // by position: a sheet being edited can name a choice twice, or leave two blank, and keys that repeat leave stale buttons behind
            key={i}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(on ? null : o.value)}
            className={cn(
              'rounded-md px-2.5 text-sm transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              size === 'sm' ? 'h-6 text-xs' : 'h-7',
              on ? 'bg-background font-medium text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A names or numbers list: typed in freely, or picked from the defaults when they are the only values allowed. */
export function ChipsControl({ id, input, value, disabled, onChange }: { id?: string; input: NamesInput | NumbersInput; value: Array<string | number>; disabled?: boolean; onChange: (value: Array<string | number>) => void }) {
  if (input.onlyDefaults) return <PicksControl id={id} input={input} value={value} disabled={disabled} onChange={onChange} />;
  return <EntryChips id={id} input={input} value={value} disabled={disabled} onChange={onChange} />;
}

/**
 * A list picked from the defaults, when they are the only values allowed:
 * every default is a chip, ticked or not; nothing can be typed in. What is
 * picked keeps the defaults' order.
 */
function PicksControl({ id, input, value, disabled, onChange }: { id?: string; input: NamesInput | NumbersInput; value: Array<string | number>; disabled?: boolean; onChange: (value: Array<string | number>) => void }) {
  const allowed: Array<string | number> = input.defaults ?? [];
  const numeric = input.kind === 'numbers';
  const unit = numeric ? (input as NumbersInput).unit : undefined;
  return (
    <div id={id} role="group" aria-label={input.label} className={cn('flex min-h-8 flex-wrap items-center gap-1.5 rounded-md border-[1px] border-input px-1.5 py-[3px]', disabled && 'opacity-50')}>
      {allowed.map((item, i) => {
        const on = value.includes(item);
        return (
          <button
            key={`${item}-${i}`}
            type="button"
            role="checkbox"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(allowed.filter((x) => (x === item ? !on : value.includes(x))))}
            className={cn(
              'inline-flex h-6 items-center gap-1 rounded-md border-[1px] px-2 text-sm transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              on ? (numeric ? 'border-transparent bg-sky-100 font-medium text-sky-900' : 'border-transparent bg-muted font-medium text-foreground') : 'border-dashed border-input text-muted-foreground hover:text-foreground',
            )}
          >
            {on && <Check className="size-3" />}
            <span className="tabular-nums">{numeric && unit ? `${item} ${unit}` : item}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * A list entered one at a time: type, press Enter (or a comma), and it
 * becomes a chip. Backspace in the empty box takes the last one back.
 * Numbers carry their unit on the chip; text that is not a number is refused.
 */
function EntryChips({ id, input, value, disabled, onChange }: { id?: string; input: NamesInput | NumbersInput; value: Array<string | number>; disabled?: boolean; onChange: (value: Array<string | number>) => void }) {
  const [draft, setDraft] = useState('');
  const [refused, setRefused] = useState(false);
  const numeric = input.kind === 'numbers';
  const unit = numeric ? (input as NumbersInput).unit : undefined;

  const add = (text: string) => {
    const parts = text.split(numeric ? /[\s,;]+/ : /[,;\n]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return true;
    const next = [...value];
    for (const part of parts) {
      const item = numeric ? parseNumber(part) : part;
      if (item === null) {
        setRefused(true);
        return false;
      }
      if (!next.includes(item)) next.push(item);
    }
    onChange(next);
    return true;
  };

  const commit = () => {
    if (add(draft)) setDraft('');
  };

  return (
    <div className={cn('flex min-h-8 flex-wrap items-center gap-1.5 rounded-md border-[1px] border-input px-1.5 py-[3px] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50', disabled && 'opacity-50')}>
      {value.map((item, i) => (
        <span key={`${item}-${i}`} className={cn('inline-flex h-6 items-center gap-1 rounded-md pl-2 text-sm font-medium', numeric ? 'bg-sky-100 text-sky-900' : 'bg-muted text-foreground', disabled && 'pr-2')}>
          <span className="tabular-nums">{numeric && unit ? `${item} ${unit}` : item}</span>
          {!disabled && (
            <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: String(item) })} className="flex size-5 items-center justify-center rounded text-muted-foreground hover:text-foreground" onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          id={id}
          value={draft}
          inputMode={numeric ? 'decimal' : undefined}
          placeholder={value.length ? I18n.get('rv2AddMore') : numeric ? I18n.get('rv2AddNumber') : I18n.get('rv2AddName')}
          aria-invalid={refused || undefined}
          className={cn('h-6 min-w-24 flex-1 border-0 bg-transparent px-1 text-sm outline-none', ENTERED, refused && 'text-destructive')}
          onChange={(e) => { setDraft(e.target.value); setRefused(false); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && draft === '' && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\s,;]/.test(text.trim())) {
              e.preventDefault();
              if (!add(text)) setDraft(text);
            }
          }}
        />
      )}
    </div>
  );
}
